// Generación de simbologías GS1 para impresión de etiquetas
// (checklist GS1 secciones 2 y 5: EAN-13, ITF-14, GS1-128, GS1 DataMatrix).
// Usa bwip-js, que valida estructura y dígitos verificadores al renderizar.
import bwipjs from 'bwip-js'
import { gs1CheckDigit, expandUPCE, isValidGS1Key } from './gs1Validation'
import { monoItem, logisticContentParts, orderAIs, splitLogisticGroups } from './zplLabel'
import { bestDatabarSegments } from './databarZpl'

export const PRINT_SYMBOLOGIES = [
  { value: 'ean13', title: 'EAN-13 (unidad comercial)' },
  { value: 'ean8', title: 'EAN-8 (unidad comercial pequeña)' },
  { value: 'upca', title: 'UPC-A (unidad comercial)' },
  { value: 'upce', title: 'UPC-E (comprimido)' },
  { value: 'itf14', title: 'ITF-14 (unidad de despacho)' },
  { value: 'gs1-128', title: 'GS1-128 (GTIN + AIs)' },
  { value: 'gs1datamatrix', title: 'GS1 DataMatrix (GTIN + AIs)' },
  { value: 'databarexpandedstacked', title: 'GS1 DataBar Expandido Apilado (GTIN + AIs)' },
  { value: 'gs1qrcode', title: 'GS1 QR (GTIN + AIs)' }
]

// Simbologías que codifican element string GS1 completo (GTIN + AIs con FNC1)
export const AI_SYMBOLOGIES = ['gs1-128', 'gs1datamatrix', 'databarexpandedstacked', 'gs1qrcode']

// Simbologías 2D (sin opciones de alto/tamaño de texto lineales)
const SYMBOLOGIES_2D = ['gs1datamatrix', 'gs1qrcode']

/**
 * Dígitos "GTIN" de un código para GENERACIÓN de simbologías: un UPC-E
 * (8 dígitos que no son EAN-8 válido) se expande a su UPC-A — su GTIN real.
 * La expansión vive solo aquí: el código guardado/mostrado nunca se altera.
 * Sin esto, rellenar el UPC-E con ceros (00000004252614) produce un GTIN-14
 * con verificador inválido y bwip-js rechaza ITF-14/GS1-128/DataMatrix.
 */
export function gtinDigits(code) {
  const d = String(code || '').replace(/\D/g, '')
  if (d.length === 8 && !isValidGS1Key(d, [8])) {
    const upca = expandUPCE(d)
    if (upca) return upca
  }
  return d
}

export function toGtin14(code) {
  return gtinDigits(code).padStart(14, '0')
}

// 'YYYY-MM-DD' (o Date) → 'YYMMDD' para AIs de fecha (11/13/15/17)
export function toGS1Date(value) {
  if (!value) return null
  const s = value instanceof Date ? value.toISOString().slice(0, 10) : String(value).slice(0, 10)
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/)
  return m ? m[1].slice(2) + m[2] + m[3] : null
}

/**
 * Arma el element string GS1 en formato con paréntesis que bwip-js
 * interpreta para gs1-128 / gs1datamatrix: (01)GTIN(17)YYMMDD(10)LOTE...
 * Nota GS1: (37) cantidad NO puede acompañar a (01) — solo a (02) en
 * etiquetas logísticas — por eso no se incluye aquí.
 */
/** Descompone "(01)0780…(17)270201(10)L1" en [{ ai, value }] (orden original). */
export function splitElementString(elementString) {
  const parts = String(elementString || '').split(/\((\d{2,4})\)/).filter(Boolean)
  const out = []
  for (let i = 0; i < parts.length; i += 2) out.push({ ai: parts[i], value: parts[i + 1] || '' })
  return out
}

/** AIs presentes en un element string, en su orden. */
export function elementStringAIs(elementString) {
  return splitElementString(elementString).map(p => p.ai)
}

/**
 * Reordena los AIs de un element string según `order` (lista de AIs). GS1 no
 * exige un orden: cualquier secuencia es válida mientras cada AI de largo
 * variable no final vaya seguido de FNC1 (lo resuelven parenToFNC1 / bwip-js).
 * Sirve para demostrar ante un evaluador que los lectores parsean bien
 * (p. ej. (10) al comienzo o al medio). Los AIs no listados conservan su
 * posición relativa al final.
 */
export function reorderElementString(elementString, order) {
  if (!order || !order.length) return elementString
  const parts = splitElementString(elementString)
  const rank = ai => { const i = order.indexOf(ai); return i < 0 ? order.length : i }
  const sorted = parts.map((p, i) => ({ p, i })).sort((a, b) => rank(a.p.ai) - rank(b.p.ai) || a.i - b.i)
  return sorted.map(({ p }) => `(${p.ai})${p.value}`).join('')
}

export function buildGS1ElementString({ gtin, lot, expiry, productionDate, packagingDate, bestBefore, serial, elementString, aiOrder }) {
  return reorderElementString(buildNaturalElementString({ gtin, lot, expiry, productionDate, packagingDate, bestBefore, serial, elementString }), aiOrder)
}

// Element string en orden natural: fiel al escaneo si existe, si no (01) +
// fechas + lote + serie
function buildNaturalElementString({ gtin, lot, expiry, productionDate, packagingDate, bestBefore, serial, elementString }) {
  // Si viene el element string reconstruido del escaneo original, ese manda
  // (conserva TODOS los AIs en el orden en que venían en el código leído)
  if (elementString) return elementString

  const parts = [`(01)${toGtin14(gtin)}`]
  const prod = toGS1Date(productionDate)
  const pack = toGS1Date(packagingDate)
  const bb = toGS1Date(bestBefore)
  const exp = toGS1Date(expiry)
  if (prod) parts.push(`(11)${prod}`)
  if (pack) parts.push(`(13)${pack}`)
  if (bb) parts.push(`(15)${bb}`)
  if (exp) parts.push(`(17)${exp}`)
  if (lot) parts.push(`(10)${String(lot).slice(0, 20)}`)
  if (serial) parts.push(`(21)${String(serial).slice(0, 20)}`)
  return parts.join('')
}

/**
 * Genera un SSCC-18 (AI 00): dígito de extensión + prefijo de compañía GS1 +
 * referencia serial + dígito verificador. extension(1) + prefix + serial = 17 dígitos.
 */
export function generateSSCC(extensionDigit, companyPrefix, serialRef) {
  const ext = String(extensionDigit ?? 0).slice(0, 1)
  const prefix = String(companyPrefix || '').replace(/\D/g, '')
  const serialLen = 16 - prefix.length
  if (serialLen < 1) throw new Error('Prefijo de compañía GS1 demasiado largo para SSCC')
  const serial = String(serialRef || '').replace(/\D/g, '').padStart(serialLen, '0').slice(-serialLen)
  const body = ext + prefix + serial
  return body + gs1CheckDigit(body)
}

/**
 * Genera un GSIN-17 (AI 402): prefijo GS1 + referencia serial + verificador.
 * prefix + serial = 16 dígitos. Agrupa ≥2 SSCC de un mismo envío.
 * Espejo del `generate_gsin` del backend (gs1_validation.py).
 */
export function generateGSIN(companyPrefix, serialRef) {
  const prefix = String(companyPrefix || '').replace(/\D/g, '')
  const serialLen = 16 - prefix.length
  if (serialLen < 1) throw new Error('Prefijo de compañía GS1 demasiado largo para GSIN')
  const serial = String(serialRef || '').replace(/\D/g, '').padStart(serialLen, '0').slice(-serialLen)
  const body = prefix + serial
  return body + gs1CheckDigit(body)
}

// Texto a codificar según la simbología elegida. Las formas GTIN (EAN-13/
// UPC-A/ITF-14) salen del GTIN-14 canónico (expande UPC-E, conserva el
// verificador real); EAN-8 y UPC-E usan el código crudo (claves propias).
export function barcodeTextFor(symbology, data) {
  const raw = String(data.gtin || '').replace(/\D/g, '')
  if (AI_SYMBOLOGIES.includes(symbology)) {
    return buildGS1ElementString(data)
  }
  const g14 = toGtin14(data.gtin)
  switch (symbology) {
    case 'ean13':
      return g14.slice(-13)
    case 'ean8':
      return raw.length > 8 ? raw.slice(-8) : raw
    case 'upca':
      return g14.slice(-12)
    case 'upce':
      return raw.length > 8 ? raw.slice(-8) : raw
    case 'itf14':
      return g14
    default:
      return gtinDigits(data.gtin)
  }
}

// ¿La opción está disponible para este código? Las simbologías de largo fijo
// exigen ese largo exacto (se aceptan ceros GS1 a la izquierda de un GTIN-14).
export function symbologyAvailable(symbology, barcode) {
  const raw = String(barcode || '').replace(/\D/g, '')
  if (raw.length === 0 || raw.length > 14) return false
  // UPC-E real (8 díg., no EAN-8 válido, expansión con verificador OK): sus
  // largos se evalúan sobre el UPC-A expandido. EAN-8 queda descartado para
  // un UPC-E (son claves DISTINTAS, no hay forma EAN-8 de un UPC-E) y un
  // EAN-8 válido ya no ofrece UPC-E (antes bastaba empezar con 0/1 y el
  // render fallaba por verificador).
  const isUpce = raw.length === 8 && !isValidGS1Key(raw, [8]) && !!expandUPCE(raw)
  const digits = isUpce ? expandUPCE(raw) : raw
  // ¿Puede reducirse a n dígitos quitando solo ceros a la izquierda?
  const fitsIn = n => digits.length === n || (digits.length > n && /^0+$/.test(digits.slice(0, digits.length - n)))
  switch (symbology) {
    case 'ean13':
      return fitsIn(13)
    case 'ean8':
      return !isUpce && fitsIn(8)
    case 'upca':
      return fitsIn(12)
    case 'upce':
      return isUpce
    default:
      return true
  }
}

const SIZE_OPTS = {
  small: { scale: 1, height: 8, textsize: 8 },
  medium: { scale: 2, height: 10, textsize: 9 },
  large: { scale: 2, height: 14, textsize: 10 }
}

/**
 * Renderiza la simbología en un canvas. Lanza si los datos no son válidos
 * para esa simbología (bwip-js valida estructura + check digit).
 */
export function renderBarcodeToCanvas(canvas, symbology, data, labelSize = 'medium') {
  const size = SIZE_OPTS[labelSize] || SIZE_OPTS.medium
  const opts = {
    bcid: symbology,
    text: barcodeTextFor(symbology, data),
    includetext: true,
    textxalign: 'center',
    scale: size.scale,
    paddingwidth: 4,
    paddingheight: 2,
    backgroundcolor: 'FFFFFF'
  }
  if (!SYMBOLOGIES_2D.includes(symbology)) {
    opts.height = size.height
    opts.textsize = size.textsize
  }
  if (symbology === 'databarexpandedstacked') {
    // Segmentos por fila balanceados (mismo criterio que la impresión ZPL en
    // databarZpl.js): mínimo de filas y filas parejas. El default de BWIPP
    // (4) apila 5+ filas con element strings largos; 10 fijo dejaba una fila
    // entera y otra con 1 segmento. El alto por fila lo define la simbología.
    opts.segments = bestDatabarSegments(opts.text).segments
    delete opts.height
  }
  bwipjs.toCanvas(canvas, opts)
  return canvas
}

// Render a dataURL PNG (para armar el HTML de impresión)
export function renderBarcodeToDataURL(symbology, data, labelSize = 'medium') {
  const canvas = document.createElement('canvas')
  renderBarcodeToCanvas(canvas, symbology, data, labelSize)
  return canvas.toDataURL('image/png')
}

// Code 128 COMÚN (no GS1-128: sin FNC1 ni AIs) para referencias internas.
// Se usa en etiquetas de empresas sin prefijo GS1: un lector aguas abajo
// no debe confundir la referencia interna con una clave GS1.
export function renderCode128DataURL(text, { scale = 2, height = 12 } = {}) {
  const canvas = document.createElement('canvas')
  bwipjs.toCanvas(canvas, {
    bcid: 'code128',
    text: String(text),
    includetext: true,
    textxalign: 'center',
    scale,
    height,
    paddingwidth: 6,
    paddingheight: 2,
    backgroundcolor: 'FFFFFF'
  })
  return canvas.toDataURL('image/png')
}

// Render directo de un element string GS1 (con paréntesis) a GS1-128 dataURL.
// Sirve para (00)SSCC y (02)GTIN(37)qty de la etiqueta logística.
export function renderGS1128DataURL(elementString, { scale = 2, height = 12 } = {}) {
  const canvas = document.createElement('canvas')
  bwipjs.toCanvas(canvas, {
    bcid: 'gs1-128',
    text: elementString,
    includetext: true,
    textxalign: 'center',
    scale,
    height,
    paddingwidth: 6,
    paddingheight: 2,
    backgroundcolor: 'FFFFFF'
  })
  return canvas.toDataURL('image/png')
}

/**
 * HTML imprimible de la ETIQUETA LOGÍSTICA GS1 (formato 4×6") de un envío,
 * según el modelo GS1 de unidades logísticas:
 *   1. Información corporativa (logo + nombre + dirección/fono/web del emisor)
 *   2. Información visual legible — monoproducto: producto, SSCC, GTIN, lote,
 *      vencimiento, cantidad; mixto: "Productos Varios" + SSCC
 *   3. Captura de datos — monoproducto: GS1-128 (02)(17)(10)(37) + (00)SSCC;
 *      mixto: solo (00)SSCC
 * Solo claves GS1 (sin códigos internos/SKU).
 * `packingList` es el JSON de GET .../packing-list (incluye company del
 * endpoint /company/settings).
 */
/**
 * HTML imprimible del PACKING LIST MÚLTIPLE (A4): agrupa ≥2 SSCC bajo un
 * GSIN. A diferencia del PDF del servidor, este documento lleva los CÓDIGOS
 * DE BARRAS reales (bwip-js): GS1-128 (00) por pallet y GS1-128 (402) del
 * GSIN al final — el GSIN no se pega en bultos, viaja en la documentación.
 * `data` es el JSON de GET /egresos/packing-list-multiple.
 */
export function buildMultiPackingListHTML(data) {
  const co = data.company || {}
  const shipments = data.shipments || []
  const gsin = data.gsin

  const bloques = shipments.map((sh, idx) => {
    let ssccImg = ''
    try {
      ssccImg = `<div class="bc"><img src="${renderGS1128DataURL(`(00)${sh.sscc}`, { scale: 2, height: 11 })}"/></div>`
    } catch (e) { /* sin barcode si el SSCC no renderiza */ }
    const filas = (sh.items || []).map(it => `
        <tr>
          <td>${it.gtin14 || '—'}</td><td>${it.gtin13 || '—'}</td>
          <td>${it.description || '—'}</td><td>${it.lot_number || '—'}</td>
          <td>${it.expiry_date || '—'}</td><td class="num">${it.quantity || 0}</td>
        </tr>`).join('')
    const peso = sh.net_weight_kg ? ` · Peso neto: ${sh.net_weight_kg} kg` : ''
    return `
      <div class="pallet">
        <h3>PALLET ${idx + 1} — SSCC: ${sh.sscc}</h3>
        <div class="sub">Egreso ${sh.order_number || ''} · Destinatario: ${sh.customer_name || '—'} · Fecha: ${sh.date || ''}${peso}</div>
        ${ssccImg}
        <table>
          <thead><tr><th>GTIN-14</th><th>GTIN-13</th><th>Descripción</th><th>Lote</th><th>Vencimiento</th><th>Cant.</th></tr></thead>
          <tbody>${filas}</tbody>
        </table>
      </div>`
  }).join('')

  const totalUnidades = shipments.reduce((s, sh) => s + (sh.total_units || 0), 0)
  let gsinImg = ''
  try {
    gsinImg = `<div class="bc"><img src="${renderGS1128DataURL(`(402)${gsin}`, { scale: 2, height: 13 })}"/></div>`
  } catch (e) { /* sin barcode */ }

  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Packing List múltiple GSIN ${gsin}</title>
  <style>
    @page { size: A4; margin: 14mm; }
    body { font-family: Helvetica, Arial, sans-serif; font-size: 12px; color: #111; }
    h1 { font-size: 19px; margin: 2px 0 4px; }
    h3 { margin: 12px 0 2px; }
    .emisor { font-weight: bold; font-size: 14px; }
    .sub { color: #333; font-size: 11px; margin-bottom: 4px; }
    table { border-collapse: collapse; width: 100%; margin-top: 4px; }
    th { background: #1a237e; color: #fff; padding: 4px 6px; font-size: 11px; text-align: left; }
    td { border: 1px solid #999; padding: 4px 6px; font-size: 11px; }
    td.num { text-align: right; }
    .bc { margin: 6px 0 2px; }
    .bc img { max-width: 78mm; }
    .gsin { margin-top: 18px; text-align: center; border-top: 2px solid #000; padding-top: 10px; }
    .gsin h2 { margin: 4px 0; letter-spacing: 1px; }
    .nota { font-size: 10px; color: #333; }
  </style></head><body>
  <div class="emisor">${co.name || 'Empresa emisora'}</div>
  <h1>PACKING LIST MÚLTIPLE — ENVÍO AGRUPADO GS1</h1>
  <div class="sub">Agrupa ${shipments.length} unidades logísticas (SSCC) bajo un mismo número global de identificación de envío (GSIN).</div>
  ${bloques}
  <div class="sub" style="margin-top:8px"><b>Total del envío:</b> ${shipments.length} pallets · ${totalUnidades} unidades</div>
  <div class="gsin">
    <h2>GSIN: ${gsin}</h2>
    ${gsinImg}
    <div class="nota">El GSIN identifica el envío completo ante transportista y destinatario.
    No se adhiere a ningún bulto físico: acompaña esta documentación.</div>
  </div>
  <script>window.onload = () => setTimeout(() => window.print(), 400)<\/script>
  </body></html>`
}

export function buildLogisticLabelHTML(packingList, extraAI = null, options = {}) {
  // extraAI: { ai: '90'..'99', value } — AI de uso interno opcional en la
  // etiqueta (observación 1ª evaluación GS1). Va al element string de
  // contenido (mono-producto) y siempre como texto visible.
  const sh = packingList.shipment || {}
  const items = packingList.items || []
  const co = packingList.company || {}
  const companyName = co.name || 'Empresa emisora'
  const companyLines = [
    [co.address, co.city, co.country].filter(Boolean).join(' - '),
    [co.phone && `Fono: ${co.phone}`, co.website].filter(Boolean).join(' · ')
  ].filter(Boolean)
  const logoHtml = co.logo_data
    ? `<img class="logo" src="${co.logo_data}" alt="logo"/>`
    : `<div class="logo-txt">${companyName}</div>`

  const it = monoItem(items)
  const mono = !!it
  // Contenido declarado de la unidad logística (checklist 4.2): si el pallet
  // se armó por CAJAS, el (02) es el GTIN de la caja y el (37) el n° de cajas
  const content = sh.content || null
  const byCase = !!(content && content.level === 'case')
  const contentG14 = (content && content.gtin14) || (it && it.gtin14) || null
  const contentCount = (content && content.count) || (it && it.quantity) || 1

  // Peso del pallet (salida del SSCC — observación 1ª evaluación GS1)
  const netKg = sh.net_weight_kg
  const grossKg = sh.gross_weight_kg || netKg
  const pesoLinea = netKg
    ? `<div class="kv"><b>Peso neto:</b> ${netKg} kg &nbsp;&nbsp;<b>Peso bruto:</b> ${grossKg} kg</div>`
    : ''

  // AI de uso interno 90-99 (opcional)
  const extraOk = extraAI && /^9[0-9]$/.test(String(extraAI.ai)) && extraAI.value
  const extraValue = extraOk ? String(extraAI.value).slice(0, 30) : null
  const extraLinea = extraOk
    ? `<div class="kv"><b>Uso interno:</b> ${extraValue}</div>`
    : ''

  // Con prefijo GS1 → SSCC (00) en GS1-128. Sin prefijo (empresa no asociada)
  // → referencia interna en Code 128 común, marcada como NO-GS1.
  const hasSscc = !!sh.sscc
  const internalRef = sh.order_number || sh.picking_list_number || ''
  const idImg = hasSscc
    ? renderGS1128DataURL(`(00)${sh.sscc}`, { scale: 2, height: 13 })
    : (internalRef ? renderCode128DataURL(internalRef, { scale: 2, height: 13 }) : '')

  // Sección visual + barcode de contenido (solo monoproducto)
  let visualRows
  let contentBarcode = ''
  if (mono) {
    const venc = it.expiry_date ? String(it.expiry_date).slice(0, 10) : null
    visualRows = `
      <div class="kv"><b>Producto:</b> ${it.description || ''}</div>
      ${hasSscc
        ? `<div class="kv"><b>SSCC:</b> <span class="hl">${sh.sscc}</span></div>`
        : `<div class="kv"><b>Ref. interna:</b> <span class="hl">${internalRef}</span></div>`}
      <div class="kv"><b>${byCase ? 'GTIN caja' : 'GTIN'}:</b> ${contentG14 || ''} &nbsp;&nbsp;<b>Lote:</b> ${it.lot_number || '—'}</div>
      <div class="kv"><b>Fecha Vencimiento:</b> ${venc || '—'} &nbsp;&nbsp;<b>Cant:</b> ${byCase ? `${contentCount} cajas (${it.quantity} un.)` : it.quantity}</div>
      ${pesoLinea}${extraLinea}`
    // (02) = GTIN del contenido (caja si el pallet se armó por unidades de
    // despacho — checklist 4.2); (37) cuenta en esa misma unidad
    // Mismos AIs, orden y corte que la etiqueta ZPL: máx. 48 caracteres
    // legibles (con AIs y paréntesis) por GS1-128, hasta 2 símbolos
    const groups = splitLogisticGroups(
      orderAIs(logisticContentParts(packingList, extraAI), options.aiOrder || [], p => p.ai),
      { maxChars: options.maxChars || 48 })
    try {
      contentBarcode = groups
        .map(g => `<div class="bc"><img src="${renderGS1128DataURL(g.map(p => p.human).join(''), { scale: 2, height: 9 })}"/></div>`)
        .join('')
    } catch (e) {
      contentBarcode = ''
    }
  } else {
    // Pallet MIXTO: solo SSCC + peso (observación 1ª evaluación GS1)
    visualRows = `
      <div class="kv"><b>Producto:</b> Productos Varios</div>
      ${hasSscc
        ? `<div class="kv"><b>SSCC:</b> <span class="hl">${sh.sscc}</span></div>`
        : `<div class="kv"><b>Ref. interna:</b> <span class="hl">${internalRef}</span></div>`}
      ${pesoLinea}${extraLinea}`
  }

  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Etiqueta ${hasSscc ? 'SSCC ' + sh.sscc : internalRef}</title>
  <style>
    /* Etiqueta 10×10 cm (media real: rollo 100×100 mm de la GK420t).
       Todo el contenido debe caber en UNA etiqueta física — si la página
       supera el largo del rollo, la impresora corta en el borde. */
    @page { size: 100mm 100mm; margin: 3mm; }
    html, body { height: auto; }
    body { font-family: Helvetica, Arial, sans-serif; font-size: 10px; margin: 0; }
    .label { border: 2px solid #000; width: 92mm; max-height: 92mm; overflow: hidden; padding: 0; box-sizing: border-box; }
    .sec { border-bottom: 2px solid #000; padding: 4px 6px; }
    .sec:last-child { border-bottom: none; }
    .corp { display: flex; align-items: center; gap: 6px; }
    .logo { max-height: 34px; max-width: 90px; object-fit: contain; }
    .logo-txt { font-size: 13px; font-weight: bold; }
    .corp-info { font-size: 8px; line-height: 1.25; }
    .corp-name { font-size: 11px; font-weight: bold; }
    .kv { margin: 2px 0; font-size: 10px; }
    .hl { font-weight: bold; letter-spacing: 0.5px; }
    .bc { text-align: center; margin: 4px 0 1px; }
    .bc img { max-width: 100%; }
  </style></head><body>
  <div class="label">
    <div class="sec corp">
      ${logoHtml}
      <div class="corp-info">
        ${co.logo_data ? `<div class="corp-name">${companyName}</div>` : ''}
        ${companyLines.map(l => `<div>${l}</div>`).join('')}
      </div>
    </div>
    <div class="sec">
      ${visualRows}
      <div class="kv" style="font-size:9px; color:#333;">
        <b>Destinatario:</b> ${sh.customer_name || '—'} · ${sh.order_number || ''} · ${sh.date || ''}
      </div>
    </div>
    <div class="sec">
      ${contentBarcode}
      ${idImg ? `<div class="bc"><img src="${idImg}"/></div>` : ''}
      ${hasSscc ? '' : '<div style="text-align:center; font-size:8px; color:#555;">REFERENCIA INTERNA — NO ES CLAVE GS1 (empresa sin prefijo GS1)</div>'}
    </div>
  </div>
  <script>window.onload = () => setTimeout(() => window.print(), 300)<\/script>
  </body></html>`
}
